/**
 * Help and feedback: report a bug, ask for a feature, or open a support
 * ticket, and follow the replies. Bug reports open a ticket automatically.
 */

import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { Field, inputClass } from "../components/form";
import { Button, Card, Empty, Notice, PageHeader, Tag, selectClass, textareaClass } from "../components/ui";
import { describeApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useLoad } from "../lib/useLoad";

interface Ticket {
  id: string;
  subject: string;
  type: string;
  status: string;
  lastMessageAt: string;
}

interface FeedbackRow {
  id: string;
  type: "missing_feature" | "bug_report";
  title: string;
  status: string;
  createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  open: "Waiting on support",
  pending: "Support replied",
  resolved: "Resolved",
  closed: "Closed",
  new: "Received",
  reviewing: "Reviewing",
  planned: "Planned",
  in_progress: "Being built",
  done: "Done",
  declined: "Not planned",
};

export function Help() {
  const { api } = useSession();
  const tickets = useLoad(() => api<{ tickets: Ticket[] }>("/api/support/tickets"), [api]);
  const feedback = useLoad(() => api<{ feedback: FeedbackRow[] }>("/api/feedback/mine"), [api]);

  return (
    <div>
      <PageHeader title="Help and feedback" detail="Report a problem, ask for something Cubby does not do yet, or get help." />
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <FeedbackForm
            onSent={() => {
              tickets.reload();
              feedback.reload();
            }}
          />
        </div>
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <h2 className="text-lg font-semibold">Your tickets</h2>
            {tickets.error ? <Notice>{tickets.error}</Notice> : null}
            <div className="mt-3 space-y-2">
              {tickets.data?.tickets.length === 0 ? <Empty>No tickets yet.</Empty> : null}
              {tickets.data?.tickets.map((t) => (
                <Link key={t.id} to={`/help/tickets/${t.id}`} className="block rounded-xl border border-stone-200 px-4 py-3 hover:bg-stone-50">
                  <span className="block font-medium">{t.subject}</span>
                  <span className="mt-1 flex items-center gap-2 text-sm text-stone-500">
                    <Tag tone={t.status === "pending" ? "ok" : "neutral"}>{STATUS_LABEL[t.status] ?? t.status}</Tag>
                    {formatDateTime(t.lastMessageAt)}
                  </span>
                </Link>
              ))}
            </div>
          </Card>
          <Card>
            <h2 className="text-lg font-semibold">Requests you have sent</h2>
            <div className="mt-3 space-y-2">
              {feedback.data?.feedback.length === 0 ? <Empty>Nothing sent yet.</Empty> : null}
              {feedback.data?.feedback
                .filter((f) => f.type === "missing_feature")
                .map((f) => (
                  <div key={f.id} className="rounded-xl border border-stone-200 px-4 py-3">
                    <span className="block font-medium">{f.title}</span>
                    <span className="mt-1 block text-sm text-stone-500">{STATUS_LABEL[f.status] ?? f.status}</span>
                  </div>
                ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FeedbackForm({ onSent }: { onSent: () => void }) {
  const { api } = useSession();
  const [kind, setKind] = useState<"bug_report" | "missing_feature" | "question">("bug_report");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState("slows_me_down");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      if (kind === "question") {
        await api("/api/support/tickets", {
          method: "POST",
          body: JSON.stringify({ subject: title, body: description, type: "support" }),
        });
        setResult({ tone: "ok", text: "Ticket opened. Replies show up under Your tickets." });
      } else {
        await api("/api/feedback", {
          method: "POST",
          body: JSON.stringify({
            feedbackType: kind,
            title,
            description,
            workflowImpact: impact,
            pagePath: document.referrer ? new URL(document.referrer).pathname : null,
          }),
        });
        setResult({
          tone: "ok",
          text: kind === "bug_report" ? "Sent. A ticket was opened so you can follow it." : "Sent. Thank you.",
        });
      }
      setTitle("");
      setDescription("");
      onSent();
    } catch (caught) {
      setResult({ tone: "error", text: describeApiError(caught) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What is this about">
          {(
            [
              ["bug_report", "Something is broken"],
              ["missing_feature", "Cubby should do this"],
              ["question", "I need help"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={kind === value}
              onClick={() => setKind(value)}
              className={`rounded-xl border px-4 py-2 text-sm font-medium ${
                kind === value ? "border-leaf-600 bg-leaf-50 text-leaf-800" : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {result ? <Notice tone={result.tone}>{result.text}</Notice> : null}

        <Field label={kind === "question" ? "Subject" : "Short title"} htmlFor="fb-title">
          <input id="fb-title" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
        </Field>
        <Field
          label={kind === "bug_report" ? "What happened, and what did you expect?" : kind === "missing_feature" ? "What do you need, and how do you do it today?" : "How can we help?"}
          htmlFor="fb-description"
        >
          <textarea
            id="fb-description"
            required
            rows={6}
            maxLength={5000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={textareaClass}
          />
        </Field>
        {kind !== "question" ? (
          <Field label="How much does it get in the way?" htmlFor="fb-impact">
            <select id="fb-impact" value={impact} onChange={(e) => setImpact(e.target.value)} className={`${selectClass} w-full`}>
              <option value="nice_to_have">Nice to have</option>
              <option value="slows_me_down">It slows me down</option>
              <option value="blocking">I cannot do my job</option>
            </select>
          </Field>
        ) : null}
        <p className="text-sm text-stone-500">Please leave out children's names and health details. Describe the situation instead.</p>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {busy ? "Sending" : "Send"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface Thread {
  ticket: { id: string; subject: string; status: string };
  messages: { id: string; from: string; fromSupport: boolean; body: string; createdAt: string }[];
}

export function TicketThread() {
  const { id = "" } = useParams();
  const { api } = useSession();
  const { data, error, reload } = useLoad(() => api<Thread>(`/api/support/tickets/${id}`), [api, id]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  async function send(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSendError(null);
    try {
      await api(`/api/support/tickets/${id}/messages`, { method: "POST", body: JSON.stringify({ body: reply }) });
      setReply("");
      reload();
    } catch (caught) {
      setSendError(describeApiError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Link to="/help" className="text-sm font-medium text-leaf-700 hover:text-leaf-800">
        Back to help
      </Link>
      {error ? <div className="mt-4"><Notice>{error}</Notice></div> : null}
      {data ? (
        <>
          <PageHeader title={data.ticket.subject} detail={STATUS_LABEL[data.ticket.status] ?? data.ticket.status} />
          <div className="space-y-3">
            {data.messages.map((m) => (
              <div key={m.id} className={`rounded-2xl px-5 py-4 ${m.fromSupport ? "bg-leaf-50" : "border border-stone-200 bg-white"}`}>
                <p className="text-sm font-medium text-stone-700">
                  {m.from} <span className="font-normal text-stone-500">{formatDateTime(m.createdAt)}</span>
                </p>
                <p className="mt-2 whitespace-pre-wrap text-[15px]">{m.body}</p>
              </div>
            ))}
          </div>
          {data.ticket.status !== "closed" ? (
            <form onSubmit={send} className="mt-6 space-y-3">
              {sendError ? <Notice>{sendError}</Notice> : null}
              <textarea required rows={4} maxLength={10000} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply" className={textareaClass} />
              <div className="flex justify-end">
                <Button type="submit" disabled={busy}>
                  {busy ? "Sending" : "Send reply"}
                </Button>
              </div>
            </form>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
